import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Button,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  generateReport,
  resetGenerateReport,
  fetchReportById,
  clearParameterListState,
  clearBatchListForReportState,
} from "../../../redux/reports/actions";
import Label from "../../../components/Label/Label";
import CAMHeader from "../../../components/Header/CAMHeader";
import CAMDrawer from "../../../components/LeftSideBar/CAMDrawer";
import InvestigatorContainer from "../../../components/contsiners/InvestigatorContainer";
import DropdownsBar from "../header/ProductSiteUnitSelector";
import BreadcrumbsBar from "../header/BreadcrumbNav";
import StepSelectProduct from "./StepSelectProduct";
import StepSelectBatch from "./StepSelectBatch";
import StepSelectChartType from "./StepSelectChartType";
import StepSelectParameter from "./StepSelectParameter";
import CustomizedSnackbars from "../../../components/Snackbars/CustomizedSnackbars";
import "./reports.scss";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReportNameDialog from "./ReportNameDialog";
  
const Report = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { report_id, mode } = location.state || {};

  const [activeStep, setActiveStep] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [openModal, setOpenModal] = useState(false);
  // const [reportInputs, setReportInputs] = useState({
  //   name: "",
  //   description: "",
  // });

  const generateReportResponse = useSelector(
    (state) => state.reports.generatedReport,
  );

  const fetchedReport = useSelector((state) => state.reports.reportData);

  const [formData, setFormData] = useState({
    product: {
      product_id: "",
      product_name: "",
      site_id: "",
      site_name: "",
    },
    batchState: {
      batchRangeOption: "by_latest_x",
      recentBatchCount: "20",
      recentDayCount: "",
      startDate: "",
      endDate: "",
      batchIdList: [],
    },
    chartState: {
      plotType: "",
      controlChartConfigState: {
        showZoomScale: false,
        zoomYMin: "",
        zoomYMax: "",
        movingAverage: false,
        plotAggregation: false,
        ruleset: "nelson",
        selectedRules: ["rule1"],
      },
      pccChartConfigState: {},
      xBarChartConfigState: {
        selectedBatchId: "",
        ruleset: "nelson",
        selectedRules: ["rule1"],
      },
      ppChartConfigState: { selectedCurves: ["norm"] },
    },
    parametersState: [],
  });

  const [isFormValid, setIsFormValid] = useState(true);

  const steps = [
    "Select Product",
    "Select Batch",
    "Select Chart Type",
    "Select Parameter",
  ];

  const updateFormData = (path, value) => {
    setFormData((prev) => {
      const updated = { ...prev };
      let target = updated;
      for (let i = 0; i < path.length - 1; i++) {
        target[path[i]] = { ...target[path[i]] };
        target = target[path[i]];
      }
      target[path[path.length - 1]] = value;
      return updated;
    });
  };

  const canProceedToNextStep = () => {
    switch (activeStep) {
      case 0:
        return formData.product.product_id && formData.product.site_id;
      case 1:
        return formData.batchState.batchIdList?.length > 0;
      case 2:
        return formData.chartState.plotTypes?.length > 0;
      case 3:
        return isFormValid;
      default:
        return false;
    }
  };

  useEffect(() => {
    dispatch(resetGenerateReport());
    return () => {
      dispatch(clearParameterListState());
      dispatch(clearBatchListForReportState());

      dispatch(resetGenerateReport());
      setSnackbar({ open: false, message: "", severity: "info" });
    };
  }, [dispatch]);

  // If mode = edit → fetch report details
  useEffect(() => {
    if (mode === "edit" && report_id) {
      dispatch(fetchReportById({ report_id }));
    }
  }, [dispatch, mode, report_id]);

  // When report is fetched → populate states
  useEffect(() => {
    if (mode === "edit" && fetchedReport?.data) {
      // setReportInputs({
      //   name: "",
      //   description: "",
      // });

      setFormData({
        product: fetchedReport.data?.product || {
          product_id: "",
          product_name: "",
          site_id: "",
          site_name: "",
        },
        batchState: fetchedReport.data?.batchState || {
          batchRangeOption: "by_latest_x",
          recentBatchCount: "20",
          recentDayCount: "",
          startDate: "",
          endDate: "",
          batchIdList: [],
        },
        chartState: fetchedReport.data?.chartState || {
          plotType: "",
          controlChartConfigState: {
            showZoomScale: false,
            zoomYMin: "",
            zoomYMax: "",
            movingAverage: false,
            plotAggregation: false,
            ruleset: "nelson",
            selectedRules: ["rule1"],
          },
          pccChartConfigState: {},
          xBarChartConfigState: {},
          ppChartConfigState: { selectedCurves: ["norm"] },
        },
        parametersState: fetchedReport.data?.parametersState || [],
      });
    }
  }, [fetchedReport?.data, mode]);

  // Handle generate response
  useEffect(() => {
    if (!generateReportResponse) return;
    if (generateReportResponse.loader) {
      setSnackbar({
        open: true,
        message: "Generating report...",
        severity: "info",
      });
    }
    setTimeout(() => {
      setSnackbar({
        open: false,
        message: "",
        severity: "",
      });
    }, 3000);
    // else if (generateReportResponse.apiError) {
    //   setSnackbar({
    //     open: true,
    //     message: generateReportResponse.message || "Failed to generate report",
    //     severity: "error",
    //   });
    // } else if (generateReportResponse.apiSuccess) {
    //   setSnackbar({
    //     open: true,
    //     message:
    //       generateReportResponse.message || "Report successfully generated",
    //     severity: "success",
    //   });
    // }
  }, [generateReportResponse, navigate]);

  const handleNext = () => {
    canProceedToNextStep() && setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleCancel = () => navigate(-1);

  const handleBackPage = () => {
    navigate(-1); // goes back to previous page
  };

  const handleSaveTemplate = () =>
    localStorage.setItem("reportTemplate", JSON.stringify(formData));

  const handleGenerateReport = (reportInputs, saveAsDraft = false) => {
    const formatDatesLocal = (value) => {
      if (!value) return value;
      const date = new Date(value);
      if (isNaN(date)) return value;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const payload = {
      ...formData,
      batchState: {
        ...formData.batchState,
        startDate: formatDatesLocal(formData.batchState.startDate),
        endDate: formatDatesLocal(formData.batchState.endDate),
      },
      report_name: reportInputs.name,
      description: reportInputs.description,
      user_id: localStorage.getItem("userid"),
      user_name: localStorage.getItem("userName"),
      save_as_draft: saveAsDraft,
    };

    dispatch(generateReport(payload));
    setOpenModal(false);

    setTimeout(() => {
      navigate("/analytics/reports");
    }, 3000);
  };

  const renderStepContent = (step) => (
    <Paper sx={{ p: 1, height: "100% !important" }} elevation={0}>
      {(() => {
        switch (step) {
          case 0:
            return (
              <StepSelectProduct
                formData={formData}
                setFormData={setFormData}
              />
            );
          case 1:
            return (
              <StepSelectBatch
                formData={formData}
                onChange={(updatedFields) =>
                  updateFormData(["batchState"], {
                    ...formData.batchState,
                    ...updatedFields,
                  })
                }
                setFormData={(values) => setFormData(values)}
                mode={mode}
              />
            );
          case 2:
            return (
              <StepSelectChartType
                chartState={formData.chartState}
                batchIdList={formData.batchState.batchIdList}
                onChange={(val) =>
                  updateFormData(["chartState"], {
                    ...formData.chartState,
                    ...val,
                  })
                }
              />
            );
          case 3:
            return (
              <StepSelectParameter
                formData={formData}
                prefilledParameters={formData.parametersState}
                onChange={(val) => updateFormData(["parametersState"], val)}
                onValidate={setIsFormValid}
              />
            );
          default:
            return <Label text="Invalid Step" />;
        }
      })()}
    </Paper>
  );

  return (
    <div>
      <CAMHeader />
      <CAMDrawer />
      <InvestigatorContainer>
        <DropdownsBar disabledFromParent={true} />
        <Box>
          <Divider sx={{ mx: 2 }} />

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              // gap: 1
            }}
            px={2}
            py={0.5}
          >
            <Chip
              icon={
                <ArrowBackIcon
                  style={{ color: "#181D27", fontSize: 16, marginRight: 4 }}
                />
              }
              label={<Label text="Back" color="#181D27" />}
              onClick={handleBackPage}
              sx={{
                height: 34,
                fontSize: 12,
                padding: "0 12px",
                "& .MuiChip-label": {
                  padding: 0.5,
                  display: "flex",
                  alignItems: "center",
                },
              }}
            />

            <BreadcrumbsBar />
          </Box>

          <Divider sx={{ mx: 2 }} />
        </Box>

        <Box
          className="new-report-container"
          sx={{ display: "flex", flexDirection: "column", minHeight: "100%" }}
        >
          <Box sx={{ width: "100%", mt: 1 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>
                    <Label text={label} />
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Divider sx={{ my: 1, mx: 1 }} />
          <Box
            sx={{
              flexGrow: 1,
              height: "100%",
              overflow: "auto",
              paddingLeft: "8px",
            }}
          >
            {renderStepContent(activeStep)}
          </Box>
          <Divider sx={{ my: 1, mx: 1 }} />

          <Box
            sx={{
              py: "4px",
              px: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              disabled={
                activeStep !== steps.length - 1 ||
                generateReportResponse?.loader ||
                !isFormValid
              }
              onClick={() => setOpenModal("save_template")}
            >
              <Label
                text="Save Template"
                color={
                  activeStep !== steps.length - 1 ||
                  generateReportResponse?.loader ||
                  !isFormValid
                    ? "gray"
                    : "inherit"
                }
              />
            </Button>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button variant="outlined" color="primary" onClick={handleCancel}>
                <Label text="Cancel" color="inherit" />
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleBack}
                disabled={activeStep === 0}
              >
                <Label
                  text="Previous"
                  color={activeStep === 0 ? "gray" : "inherit"}
                />
              </Button>
              {activeStep < steps.length - 1 ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                  disabled={!canProceedToNextStep()}
                >
                  <Label
                    text="Next"
                    color={!canProceedToNextStep() ? "inherit" : "#fff"}
                  />
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setOpenModal(true)}
                  disabled={
                    generateReportResponse?.loader ||
                    !isFormValid ||
                    formData.parametersState.length === 0
                  }
                  startIcon={
                    generateReportResponse?.loader && (
                      <CircularProgress size={18} color="inherit" />
                    )
                  }
                >
                  {generateReportResponse?.loader
                    ? "Generating..."
                    : "Generate Report"}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </InvestigatorContainer>

      <ReportNameDialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSubmit={handleGenerateReport}
        isTemplate={openModal === "save_template" ? true : false}
        mode={mode}
      />

      {snackbar.open && (
        <CustomizedSnackbars
          severity={snackbar.severity}
          state={snackbar.open}
          text={snackbar.message}
          handleClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        />
      )}
    </div>
  );
};

export default Report;
